<template>
  <div class="adeal">
    <p class="adeal__lead">
      첫 번째 상품이 몰 홈 「이달의 특가」 히어로로 크게 노출됩니다. 비워 두면 판매가 많은 상품이
      자동으로 채워집니다.
    </p>

    <p v-if="errorMessage" class="adeal__error">
      <AppIcon icon="mdi-alert-outline" :size="16" />
      {{ errorMessage }}
    </p>

    <div class="adeal__grid">
      <section class="sui-card sui-card--flush" data-tour="admin-deals-list">
        <div class="sui-toolbar">
          <strong class="adeal__section-title">노출 목록 {{ draft.length }}개</strong>
          <span class="sui-toolbar__spacer" />
          <button type="button" class="sui-btn" :disabled="loading || !dirty" @click="reload">
            변경 취소
          </button>
          <button
            type="button"
            class="sui-btn sui-btn--primary"
            :disabled="saving || !dirty"
            @click="onSave"
          >
            {{ saving ? '저장 중…' : '특가 저장' }}
          </button>
        </div>
        <div class="sui-toolbar adeal__period">
          <label class="adeal__period-field">
            <span>특가 시작</span>
            <input v-model="dealStartAt" type="datetime-local" />
          </label>
          <label class="adeal__period-field">
            <span>특가 종료</span>
            <input v-model="dealEndAt" type="datetime-local" />
          </label>
          <p class="adeal__period-hint">비우면 상시 노출입니다. 저장 시 목록 전체에 적용됩니다.</p>
        </div>

        <div v-if="draft.length" class="adeal__list">
          <article
            v-for="(product, index) in draft"
            :key="product.code"
            class="adeal__item"
            draggable="true"
            @dragstart="onDragStart(index)"
            @dragover.prevent
            @drop="onDrop(index)"
          >
            <span class="adeal__rank" :class="{ 'is-hero': index === 0 }" title="끌어다 순서를 바꿀 수 있습니다">
              {{ index === 0 ? '히어로' : index + 1 }}
            </span>
            <span class="sui-prod">
              <img
                v-if="product.thumbnail"
                class="sui-prod__img"
                :src="product.thumbnail"
                :alt="product.name"
                loading="lazy"
              />
              <span v-else class="adeal__noimg">
                <AppIcon icon="mdi-image-off-outline" :size="16" />
              </span>
              <span>
                <span class="sui-prod__name">{{ product.name }}</span>
                <span class="sui-table__sub">
                  {{ product.code }} · {{ product.storeName }} · {{ formatWon(product.price) }}원
                </span>
              </span>
            </span>
            <div class="adeal__item-actions">
              <button
                type="button"
                class="sui-btn sui-btn--sm"
                :disabled="index === 0"
                aria-label="위로"
                @click="move(index, -1)"
              >
                <AppIcon icon="mdi-chevron-up" :size="16" />
              </button>
              <button
                type="button"
                class="sui-btn sui-btn--sm"
                :disabled="index === draft.length - 1"
                aria-label="아래로"
                @click="move(index, 1)"
              >
                <AppIcon icon="mdi-chevron-down" :size="16" />
              </button>
              <button type="button" class="sui-btn sui-btn--sm" @click="removeAt(index)">제외</button>
            </div>
          </article>
        </div>

        <div v-else class="sui-empty">
          <AppIcon icon="mdi-sale-outline" :size="32" />
          <p>{{ loading ? '불러오는 중입니다…' : '지정된 특가 상품이 없습니다.' }}</p>
        </div>
      </section>

      <section class="sui-card sui-card--flush" data-tour="admin-deals-picker">
        <div class="sui-toolbar">
          <strong class="adeal__section-title">상품 추가</strong>
        </div>
        <div class="sui-toolbar adeal__search-bar">
          <label class="sui-search">
            <AppIcon icon="mdi-magnify" :size="17" />
            <input
              v-model.trim="keyword"
              type="search"
              placeholder="판매중 상품명 · 코드 · 업체 검색"
              @keydown.enter="searchCandidates"
            />
          </label>
          <button type="button" class="sui-btn" :disabled="searching" @click="searchCandidates">
            검색
          </button>
        </div>

        <div v-if="candidates.length" class="adeal__candidates">
          <article v-for="product in candidates" :key="product.code" class="adeal__candidate">
            <span class="sui-prod">
              <img
                v-if="product.thumbnail"
                class="sui-prod__img"
                :src="product.thumbnail"
                :alt="product.name"
                loading="lazy"
              />
              <span v-else class="adeal__noimg">
                <AppIcon icon="mdi-image-off-outline" :size="16" />
              </span>
              <span>
                <span class="sui-prod__name">{{ product.name }}</span>
                <span class="sui-table__sub">
                  {{ product.code }} · {{ formatWon(product.price) }}원
                </span>
              </span>
            </span>
            <button
              type="button"
              class="sui-btn sui-btn--sm sui-btn--primary"
              :disabled="isSelected(product.code) || draft.length >= MAX_DEALS"
              @click="addProduct(product)"
            >
              {{ isSelected(product.code) ? '추가됨' : '추가' }}
            </button>
          </article>
        </div>

        <div v-else class="sui-empty">
          <AppIcon icon="mdi-magnify" :size="28" />
          <p>{{ searching ? '검색 중…' : '판매중 상품을 검색해 특가에 추가하세요.' }}</p>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AppIcon from '@/components/common/AppIcon.vue'
import {
  fetchAdminDeals,
  fetchAdminProducts,
  saveAdminDeals,
} from '@/api/admin'
import type { ServerMallProduct } from '@/api/mallProduct'
import { useAdminProducts } from '@/composables/useAdminProducts'
import { formatWon } from '@/constants/sellerData'

const MAX_DEALS = 12

const { notify } = useAdminProducts()

const draft = ref<ServerMallProduct[]>([])
const savedCodes = ref<string[]>([])
const savedPeriod = ref({ startAt: '', endAt: '' })
const dealStartAt = ref('')
const dealEndAt = ref('')
const candidates = ref<ServerMallProduct[]>([])
const keyword = ref('')
const loading = ref(false)
const searching = ref(false)
const saving = ref(false)
const errorMessage = ref('')

function toLocalInput(value?: string | null): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function toServerDate(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`
}

const dirty = computed(() => {
  const current = draft.value.map((item) => item.code).join('|')
  return (
    current !== savedCodes.value.join('|') ||
    dealStartAt.value !== savedPeriod.value.startAt ||
    dealEndAt.value !== savedPeriod.value.endAt
  )
})

function isSelected(code: string): boolean {
  return draft.value.some((item) => item.code === code)
}

const dragIndex = ref<number | null>(null)

function move(index: number, delta: number) {
  const next = index + delta
  if (next < 0 || next >= draft.value.length) return
  const list = [...draft.value]
  const [item] = list.splice(index, 1)
  list.splice(next, 0, item)
  draft.value = list
}

function onDragStart(index: number) {
  dragIndex.value = index
}

function onDrop(index: number) {
  if (dragIndex.value === null || dragIndex.value === index) return
  const list = [...draft.value]
  const [item] = list.splice(dragIndex.value, 1)
  list.splice(index, 0, item)
  draft.value = list
  dragIndex.value = null
}

function removeAt(index: number) {
  const item = draft.value[index]
  if (!item) return
  if (!window.confirm(`'${item.name}'을(를) 특가 목록에서 제외할까요?`)) return
  draft.value = draft.value.filter((_, i) => i !== index)
}

function addProduct(product: ServerMallProduct) {
  if (isSelected(product.code) || draft.value.length >= MAX_DEALS) return
  if (!window.confirm(`'${product.name}'을(를) 특가에 추가할까요?`)) return
  draft.value = [...draft.value, product]
}

async function reload() {
  loading.value = true
  errorMessage.value = ''
  try {
    const products = await fetchAdminDeals()
    draft.value = products
    savedCodes.value = products.map((item) => item.code)
    const first = products[0]
    dealStartAt.value = toLocalInput(first?.dealStartAt)
    dealEndAt.value = toLocalInput(first?.dealEndAt)
    savedPeriod.value = { startAt: dealStartAt.value, endAt: dealEndAt.value }
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '목록을 불러오지 못했습니다.'
  } finally {
    loading.value = false
  }
}

async function searchCandidates() {
  searching.value = true
  errorMessage.value = ''
  try {
    const { products } = await fetchAdminProducts({
      status: 'SELLING',
      keyword: keyword.value,
    })
    candidates.value = products.slice(0, 30)
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '상품 검색에 실패했습니다.'
  } finally {
    searching.value = false
  }
}

async function onSave() {
  if (!window.confirm('이달의 특가 구성을 저장할까요?')) return
  saving.value = true
  errorMessage.value = ''
  try {
    const { products, message } = await saveAdminDeals(
      draft.value.map((item) => item.code),
      {
        startAt: toServerDate(dealStartAt.value),
        endAt: toServerDate(dealEndAt.value),
      },
    )
    draft.value = products
    savedCodes.value = products.map((item) => item.code)
    dealStartAt.value = toLocalInput(products[0]?.dealStartAt)
    dealEndAt.value = toLocalInput(products[0]?.dealEndAt)
    savedPeriod.value = { startAt: dealStartAt.value, endAt: dealEndAt.value }
    notify(message || '이달의 특가를 저장했습니다.')
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : '저장에 실패했습니다.'
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  await reload()
  await searchCandidates()
})
</script>

<style lang="scss" src="./AdminDealsPage.scss"></style>
